import * as React from 'react';

/**
 * Align the requested chapter directly under the report navigation.
 *
 * The report's own content is what decides where a chapter STARTS, and that
 * content keeps growing after the first paint: the map, the charts and the
 * tables each land at their own pace. A chapter that begins below the target
 * therefore drifts DOWN after the scroll that put it in place, and a landing
 * computed once is only correct if nothing above it grew afterwards. The
 * caller keeps the request pending (see `useSectionAlignment`) so this
 * runs again whenever the report's own height changes, until the owner takes
 * the scroll back. `stand-detail.md` §6: "A link can point straight at the
 * simulation, landing the owner ON it."
 */
export interface ReportScrollWrite {
  owner: HTMLElement;
  from: number;
  to: number;
  layout: LandingLayout;
}

/**
 * The geometry a landing was computed from, measured so that it does not
 * change when only the scroll position does: the target in the scroll owner's
 * content frame, the pinned navigation's height, and the owner's box. The
 * navigation's height, not its position, because a sticky rail that has not
 * docked yet moves with the scroll.
 */
interface LandingLayout {
  targetTop: number;
  navigationHeight: number | null;
  clientWidth: number;
  clientHeight: number;
  scrollHeight: number;
}

function measureLandingLayout(
  target: HTMLElement,
  scrollOwner: HTMLElement,
  navigation: HTMLElement | null,
): LandingLayout {
  return {
    targetTop:
      scrollOwner.scrollTop +
      target.getBoundingClientRect().top -
      scrollOwner.getBoundingClientRect().top,
    navigationHeight: navigation ? navigation.offsetHeight : null,
    clientWidth: scrollOwner.clientWidth,
    clientHeight: scrollOwner.clientHeight,
    scrollHeight: scrollOwner.scrollHeight,
  };
}

function currentLandingLayout(
  write: ReportScrollWrite,
  sectionId: string,
  navigation: HTMLElement | null,
): LandingLayout | null {
  const target = document.getElementById(sectionId);
  return target?.isConnected ? measureLandingLayout(target, write.owner, navigation) : null;
}

function sameLayout(a: LandingLayout, b: LandingLayout): boolean {
  return (Object.keys(a) as (keyof LandingLayout)[]).every((key) => {
    const before = a[key];
    const after = b[key];
    if (before === null || after === null) return before === after;
    return Math.abs(before - after) <= 1;
  });
}

function scrollReportToSection(
  sectionId: string,
  navigation: HTMLElement | null,
  behavior: ScrollBehavior,
): ReportScrollWrite | null {
  const target = document.getElementById(sectionId);
  const shellContent = document.querySelector<HTMLElement>('.app-shell-content');
  if (typeof shellContent?.scrollTo === 'function') shellContent.scrollTo({ top: 0 });
  else if (shellContent) shellContent.scrollTop = 0;
  const scrollOwner =
    target?.closest<HTMLElement>('.fm-workspace-shell__content') ??
    target?.closest<HTMLElement>('.app-shell-outlet');
  if (!target || !scrollOwner) return null;
  // NO FALLBACK: a target that is present but unmeasurable (detached, or laid
  // out to nothing) yields a landing computed from a zero rect, which is a
  // guess dressed as a measurement. Refuse it; the next resize brings a real
  // one.
  if (!target.isConnected || target.getBoundingClientRect().height <= 0) return null;

  const nav = navigation;
  const targetRect = target.getBoundingClientRect();
  const scrollOwnerRect = scrollOwner.getBoundingClientRect();
  const navRect = nav?.getBoundingClientRect();
  const alignedViewportTop = navRect?.bottom ?? scrollOwnerRect.top;
  const targetTop = scrollOwner.scrollTop + targetRect.top - alignedViewportTop;

  const top = Math.max(0, targetTop);
  const from = scrollOwner.scrollTop;
  const layout = measureLandingLayout(target, scrollOwner, nav);
  if (typeof scrollOwner.scrollTo === 'function') {
    scrollOwner.scrollTo({ top, behavior });
  } else {
    scrollOwner.scrollTop = top;
  }
  return { owner: scrollOwner, from, to: top, layout };
}

function isFlushUnderNavigation(sectionId: string, navigation: HTMLElement | null): boolean {
  const target = document.getElementById(sectionId);
  if (!target || !navigation) return false;
  const offset = target.getBoundingClientRect().top - navigation.getBoundingClientRect().bottom;
  return Math.abs(offset) <= 2;
}

/**
 * Keep the requested chapter under the navigation while the report is still
 * growing into its final height.
 *
 * Returns a `request` callback that both scrolls and REMEMBERS the section, and
 * re-runs that alignment on every size change of the report's own section
 * column. The request is released as soon as the owner touches the page
 * themselves (wheel, touch, key, pointer press) or asks for another chapter, so
 * the report never fights the reader for the scroll position.
 */
interface SectionAlignmentOptions {
  getNavigation: () => HTMLElement | null;
  getSections: () => HTMLElement | null;
  revision: string;
  enabled: boolean;
}

export function useSectionAlignment({
  getNavigation,
  getSections,
  revision,
  enabled,
}: SectionAlignmentOptions): (sectionId: string, behavior: ScrollBehavior) => void {
  const pendingSectionRef = React.useRef<string | null>(null);
  const observerRef = React.useRef<ResizeObserver | null>(null);
  const lastWriteRef = React.useRef<ReportScrollWrite | null>(null);

  const writeScroll = React.useCallback(
    (sectionId: string, behavior: ScrollBehavior): void => {
      const write = scrollReportToSection(sectionId, getNavigation(), behavior);
      if (!write) return;
      lastWriteRef.current = write;
    },
    [getNavigation],
  );

  // Chapters are added and replaced as their data lands, so the observations are
  // re-taken whenever the report changes: a chapter that GROWS while a later one
  // SHRINKS moves the requested landing without changing the column's own
  // height. Taken OUTSIDE the observer callback -- observing a new target
  // notifies, so re-observing from the notification is a loop.
  const syncObserved = React.useCallback((): void => {
    const observer = observerRef.current;
    if (!observer) return;
    const sections = getSections();
    if (!sections) return;
    // Re-take from scratch so chapters that left the DOM are not observed for
    // the life of the mount.
    observer.disconnect();
    observer.observe(sections);
    Array.from(sections.children).forEach((child) => {
      if (child instanceof HTMLElement) observer.observe(child);
    });
  }, [getSections]);

  React.useEffect(() => {
    if (!enabled) return undefined;
    const release = (): void => {
      pendingSectionRef.current = null;
    };
    let frame = 0;
    const realign = (): void => {
      if (!pendingSectionRef.current) return;
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const pending = pendingSectionRef.current;
        if (!pending) return;
        writeScroll(pending, 'auto');
      });
    };
    // A scroll at a position this hook did not write is either the reader
    // taking the page back (a native scrollbar drag dispatches no pointer event
    // in Chromium, so the input listeners below never see it) or the browser
    // absorbing a layout change: scroll anchoring after a section above grows,
    // clamping after the content shrinks, a viewport reflow. The browser
    // dispatches that scroll in whichever frame its layout ran, which may be
    // BEFORE the ResizeObserver notification of the same change, so the order
    // of the two events cannot tell them apart. The landing geometry can: a
    // reader's scroll leaves it exactly as the last write measured it.
    const onScroll = (event: Event): void => {
      const write = lastWriteRef.current;
      const pending = pendingSectionRef.current;
      if (!write || !pending || event.target !== write.owner) return;
      const navigation = getNavigation();
      const layout = currentLandingLayout(write, pending, navigation);
      if (layout && isFlushUnderNavigation(pending, navigation)) {
        lastWriteRef.current = { ...write, to: write.owner.scrollTop, layout };
        return;
      }
      if (!layout || !sameLayout(layout, write.layout)) {
        realign();
        return;
      }
      const low = Math.min(write.from, write.to) - 2;
      const high = Math.max(write.from, write.to) + 2;
      const at = write.owner.scrollTop;
      if (at < low || at > high) release();
    };
    observerRef.current =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(realign) : null;
    // A replaced observer starts empty; observe the report again now rather
    // than waiting for the next request or revision.
    syncObserved();
    // Capture phase: a descendant that stops propagation must not be able to
    // keep the reader's own input from releasing the page.
    const options = { capture: true, passive: true } as const;
    window.addEventListener('wheel', release, options);
    window.addEventListener('touchstart', release, options);
    window.addEventListener('keydown', release, { capture: true });
    window.addEventListener('pointerdown', release, options);
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      observerRef.current?.disconnect();
      observerRef.current = null;
      lastWriteRef.current = null;
      window.removeEventListener('wheel', release, { capture: true });
      window.removeEventListener('touchstart', release, { capture: true });
      window.removeEventListener('keydown', release, { capture: true });
      window.removeEventListener('pointerdown', release, { capture: true });
      document.removeEventListener('scroll', onScroll, { capture: true });
      release();
    };
  }, [enabled, getNavigation, syncObserved, writeScroll]);

  React.useEffect(() => {
    syncObserved();
  }, [revision, syncObserved]);

  return React.useCallback(
    (sectionId: string, behavior: ScrollBehavior): void => {
      if (!enabled) return;
      pendingSectionRef.current = sectionId;
      syncObserved();
      writeScroll(sectionId, behavior);
    },
    [enabled, syncObserved, writeScroll],
  );
}
